import { useEffect, useRef } from 'react';
import { handleRefSplit, useComponentProps, WithChildren } from '@optimus-web/common';
import { Checkbox as CheckboxComponent, CheckboxProps } from '../Checkbox';
import { Identified, InputValue, Labeled, Valued } from '../types';
import {
    AvatarElement,
    CheckboxWrapper,
    ContentElement,
    IconElement,
    IconElementProps,
    LeftContentElement,
    ListItemContainerElement,
    PrimaryTextElement,
    PrimaryTextElementProps,
    RightContentElement,
    SecondaryTextElement,
} from './style';
import { ListItemVariant } from './types';

export interface ListItemFactory {
    Avatar?: React.ComponentType<React.PropsWithChildren<{}>>;
    Content?: React.ComponentType<React.PropsWithChildren<{}>>;
    Checkbox?: React.ComponentType<CheckboxProps>;
    Icon?: React.ComponentType<React.PropsWithChildren<IconElementProps>>;
    LeftContent?: React.ComponentType<React.PropsWithChildren<{}>>;
    ListItemContainer?: React.ComponentType;
    PrimaryText?: React.ComponentType<React.PropsWithChildren<PrimaryTextElementProps | {}>>;
    RightContent?: React.ComponentType<React.PropsWithChildren<{}>>;
    SecondaryText?: React.ComponentType<React.PropsWithChildren<{}>>;
}

export interface ListItemProps extends Partial<Valued>, Partial<Labeled>, Partial<Identified>, WithChildren {
    onAfterClick?: (e: React.MouseEvent, v?: InputValue) => void;
    focused?: boolean;
    onClick?: (e: React.MouseEvent, v?: InputValue) => void;
    selected?: boolean;
    onMouseEnter?: () => void;
    inactive?: boolean;
    indent?: boolean;
    shouldScrollIntoView?: boolean;
    dense?: boolean;
    leftAvatar?: React.ReactNode;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    multiselect?: boolean;
    keyboardHandled?: boolean;
    wrapText?: boolean;
    disabled?: boolean;
    depth?: number;
    leftItem?: React.ReactNode;
    rightItem?: React.ReactNode;
    innerRef?: (c: HTMLElement) => void;
    tabIndex?: number;
    role?: string;
    'aria-selected'?: boolean;
    ariaLabel?: React.ReactNode;
    variant?: ListItemVariant;
}

/**
 * @deprecated use DropdownItem.
 * Contact DS team for more information.
 */
export const listItemFactory = ({
    Avatar = AvatarElement,
    Content = ContentElement,
    Checkbox = CheckboxComponent,
    Icon = IconElement,
    LeftContent = LeftContentElement,
    ListItemContainer = ListItemContainerElement,
    PrimaryText = PrimaryTextElement,
    RightContent = RightContentElement,
    SecondaryText = SecondaryTextElement,
}: ListItemFactory = {}) => {
    const ListItem: React.FC<ListItemProps> = (props) => {
        const {
            leftAvatar,
            children,
            dense: customDense,
            primaryText,
            secondaryText,
            leftIcon,
            rightIcon,
            selected,
            multiselect,
            wrapText = false,
            leftItem,
            rightItem,
            onAfterClick,
            onClick,
            innerRef,
            onMouseEnter,
            keyboardHandled,
            disabled,
            inactive,
            indent,
            variant = 'default',
            ariaLabel,
            focused,
            shouldScrollIntoView,
            value,
            id,
            ...other
        } = props;

        const containerRef = useRef<HTMLElement | null>(null);

        useEffect(() => {
            if (focused && shouldScrollIntoView && containerRef.current) {
                containerRef.current.scrollIntoView(false);
            }
        }, [focused, shouldScrollIntoView]);

        const handleClick = (e: React.MouseEvent) => {
            onClick?.(e, value);
            onAfterClick?.(e, value);
        };

        const { dense } = useComponentProps('ListItem', { dense: customDense });

        const checkBoxWrapper = (
            <CheckboxWrapper left>
                <Checkbox tabIndex={-1} checked={!!selected} name={id} value={value} />
            </CheckboxWrapper>
        );

        const left =
            leftItem ||
            (leftAvatar && <Avatar>{leftAvatar}</Avatar>) ||
            (leftIcon && <Icon left>{leftIcon}</Icon>) ||
            (multiselect && checkBoxWrapper);

        const right = rightItem || (rightIcon && <Icon right>{rightIcon}</Icon>);

        const containerProps = {
            ...other,
            ref: handleRefSplit({ ownRef: containerRef, forwardedRef: innerRef }),
            dense,
            wrapText,
            selected,
            singleLineWithAvatar: !!leftAvatar && !secondaryText,
            twoLine: !!secondaryText,
            onClick: !disabled ? handleClick : undefined,
            onMouseEnter: !disabled ? onMouseEnter : undefined,
            keyboardHandled,
            disabled,
            inactive,
            indent,
            'data-list-container': true,
            'aria-label': ariaLabel,
            focused,
            shouldScrollIntoView,
            value,
            id,
        };

        return (
            <ListItemContainer {...containerProps}>
                {left && <LeftContent>{left}</LeftContent>}
                <Content>
                    <PrimaryText dense={!!dense} wrapText={!!wrapText} variant={variant} data-test-primary-text>
                        {primaryText}
                    </PrimaryText>
                    {secondaryText && <SecondaryText data-test-secondary-text>{secondaryText}</SecondaryText>}
                </Content>
                {right && <RightContent>{right}</RightContent>}
                {children}
            </ListItemContainer>
        );
    };

    return ListItem;
};

export default listItemFactory();
